/**
 * Export a build to DofusBook's stuffer.
 *
 * DofusBook has no import API. What it has is a `?stuff=` query parameter its
 * SPA decodes client-side: base64 of a MessagePack map. The format is
 * undocumented and unversioned — this module is a reimplementation of the
 * encoder roxxsolver ships, checked byte-for-byte against its output (see the
 * golden in `dofusbookLink.test.js`) and against the live site's decode.
 *
 * The map has six integer keys, written as strings because they come from a JS
 * object:
 *
 *   0  {dofusbookStatIndex: value}  the character sheet WITHOUT items
 *   1  [vi, sa, fo, ine, cha, age]  points invested, already as stat values
 *   2  level
 *   3  option bitfield (all our exports send 0)
 *   4  {groupIndex: count}          how many ids each slot group takes, 1 omitted
 *   5  [itemId, ...]                Ankama ids, read positionally
 *
 * Key 5 is flat, and the decoder walks it group by group using the counts in
 * key 4. So the counts are not decoration: send roxxsolver's hardcoded
 * [1,1,1,1,1,2,6,1,1,1] for a build with an empty slot and every later id
 * lands one slot early — a partial build arrives as a cape in the hat slot and
 * nothing resolves. We send the real counts instead, which is what makes a
 * half-finished build survive the trip.
 *
 * Key 0 is absolute but it is *not* the whole character: DofusBook adds key 1
 * on top of it. Subtracting the natural sheet from key 0, it reads the first
 * 100 of each characteristic as parcho and books the rest as "forgemagie
 * globale". So the invested points belong to key 1 alone — carrying them in
 * both counted them twice, and a 300-agility build arrived at 1315 instead of
 * 1015. Two consequences of that residual worth knowing:
 *
 *   - DofusBook applies 100 vitality of scrolling whether or not we ask. An
 *     unscrolled DofGen build therefore shows -100 vitality of forgemagie.
 *   - Exo PA/PM/PO have no field of their own and arrive as +1 of forgemagie
 *     each, which is what an exo is anyway. (Key 3 has ExoPA/PM/PO bits, but
 *     setting them made the live site expect a different baseline and land two
 *     points out, so we leave the bitfield at 0.)
 *
 * Per-item forgemagie, weapon element potions, class and gender have no place
 * in the format at all.
 */

import * as st from './st';
import { SLOT_IDX } from './slotIdx';
import { magingBonusExcess, magingDelta } from './itemMaging';

const DOFUSBOOK_STUFFER_URL = 'https://www.dofusbook.net/fr/equipement/dofus-stuffer/objets';

/**
 * Our `st.js` stat names, in DofusBook's own index order. Holes are stats it
 * indexes and we do not carry (renvoi, dommages/puissance de piège); the tail
 * it has no index for at all is ours to drop — % dommages finaux, puissance
 * d'arme, % résistance armes.
 */
const DOFUSBOOK_STAT_ORDER = [
    'vi', 'sa', 'fo', 'ine', 'cha', 'age',
    'pa', 'pm', 'ini', 'pp', 'po', 'inv',
    'ret_pa', 're_pa', 'ret_pme', 're_pme',
    'cri', 'so', 'tac', 'fui',
    'do', 'pui', 'do_cri', 'pods',
    'do_neu', 'do_ter', 'do_feu', 'do_eau', 'do_air',
    null, null, null,
    'do_pou',
    'do_per_so', 'do_per_ar', 'do_per_me', 'do_per_di',
    're_neu', 're_per_neu', 're_ter', 're_per_ter', 're_feu', 're_per_feu',
    're_eau', 're_per_eau', 're_air', 're_per_air',
    're_cri', 're_pou', 're_per_di', 're_per_me',
];

/** The six characteristics of key 1, in DofusBook's order. */
const DOFUSBOOK_POINT_ORDER = ['vi', 'sa', 'fo', 'ine', 'cha', 'age'];

/**
 * Our slot indices grouped the way key 5 is read: cape, coiffe, ceinture,
 * bottes, amulette, anneaux, dofus/trophées, bouclier, arme, familier.
 */
const DOFUSBOOK_SLOT_GROUPS = [
    [SLOT_IDX.CLOAK],
    [SLOT_IDX.HAT],
    [SLOT_IDX.BELT],
    [SLOT_IDX.BOOTS],
    [SLOT_IDX.AMULET],
    SLOT_IDX.RINGS,
    SLOT_IDX.TROPHUS,
    [SLOT_IDX.SHIELD],
    [SLOT_IDX.WEAPON],
    [SLOT_IDX.PET],
];

/** st.js stat name -> its characteristic id, resolved once. */
const STAT_ID_BY_NAME = new Map(st.STATS.map(stat => [stat.name, stat.id]));

/** st.js characteristic id -> DofusBook stat index, for the sheet in key 0. */
const DOFUSBOOK_INDEX_BY_STAT_ID = new Map(
    DOFUSBOOK_STAT_ORDER.flatMap((name, index) => {
        if (!name) return [];
        const statId = STAT_ID_BY_NAME.get(name);
        return statId === undefined ? [] : [[statId, index]];
    })
);

// --- MessagePack, the sliver of it this format uses -------------------------
// Positive ints, negative ints, fixstr, fixarray and fixmap/map16. Pulling a
// library in for sixty bytes of encoder would cost more than it saves.

const FIXSTR = 0xa0;
const FIXARRAY = 0x90;
const FIXMAP = 0x80;
const ARRAY16 = 0xdc;
const MAP16 = 0xde;
const UINT8 = 0xcc;
const UINT16 = 0xcd;
const UINT32 = 0xce;
const INT8 = 0xd0;
const INT16 = 0xd1;
const INT32 = 0xd2;
const FIXED_LIMIT = 16;
const POSITIVE_FIXINT_LIMIT = 128;
const NEGATIVE_FIXINT_LIMIT = -32;

function packInt(value, out) {
    if (value >= 0) {
        if (value < POSITIVE_FIXINT_LIMIT) out.push(value);
        else if (value < 0x100) out.push(UINT8, value);
        else if (value < 0x10000) out.push(UINT16, value >> 8, value & 0xff);
        else out.push(UINT32, (value >>> 24) & 0xff, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff);
        return;
    }
    if (value >= NEGATIVE_FIXINT_LIMIT) out.push(0x100 + value);
    else if (value >= -0x80) out.push(INT8, 0x100 + value);
    else if (value >= -0x8000) {
        const u = 0x10000 + value;
        out.push(INT16, u >> 8, u & 0xff);
    } else {
        const u = 0x100000000 + value;
        out.push(INT32, (u >>> 24) & 0xff, (u >>> 16) & 0xff, (u >>> 8) & 0xff, u & 0xff);
    }
}

function pack(value, out) {
    if (Array.isArray(value)) {
        // A full build is sixteen ids, so key 5 crosses out of fixarray.
        if (value.length < FIXED_LIMIT) out.push(FIXARRAY | value.length);
        else out.push(ARRAY16, value.length >> 8, value.length & 0xff);
        value.forEach(entry => pack(entry, out));
        return;
    }
    if (value !== null && typeof value === 'object') {
        const keys = Object.keys(value);
        if (keys.length < FIXED_LIMIT) out.push(FIXMAP | keys.length);
        else out.push(MAP16, keys.length >> 8, keys.length & 0xff);
        keys.forEach(key => {
            pack(key, out);
            pack(value[key], out);
        });
        return;
    }
    if (typeof value === 'string') {
        out.push(FIXSTR | value.length);
        for (let i = 0; i < value.length; i += 1) out.push(value.charCodeAt(i));
        return;
    }
    packInt(value, out);
}

function toBase64(bytes) {
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
    return window.btoa(binary);
}

// --- the build, as DofusBook wants to read it -------------------------------

/**
 * The sheet without items and without invested points — what our stat panel
 * calls Base, minus the `attributes` term, which travels in key 1 instead.
 *
 * Per-item forgemagie rides here too, as what each maged item adds over its
 * dropped self: the format has no per-item block, so DofusBook books it as
 * forgemagie globale, the way it books the exos.
 */
function baseSheet(build) {
    const sheet = {};
    const add = (source) => {
        Object.entries(source || {}).forEach(([statId, value]) => {
            const index = DOFUSBOOK_INDEX_BY_STAT_ID.get(Number(statId));
            if (index === undefined || !value) return;
            sheet[index] = (sheet[index] || 0) + value;
        });
    };
    add(st.getNaturalStats(build.lvl) || {});
    add(build.scrolls);
    // Item lines, less what goes past the forgemagie cap, as on the sheet.
    const deltas = (build.items || [])
        .map((item, slot) => {
            const lines = item ? build.getItemMaging?.(slot) : null;
            return lines ? magingDelta(item, lines) : null;
        })
        .filter(Boolean);
    deltas.forEach(add);
    add(Object.fromEntries(
        Object.entries(magingBonusExcess(null, deltas)).map(([id, excess]) => [id, -excess]),
    ));
    return sheet;
}

/** Key 5 and key 4 together: the ids, and how many each group claims. */
function itemGroups(build) {
    const ids = [];
    const counts = {};
    DOFUSBOOK_SLOT_GROUPS.forEach((slots, group) => {
        const groupIds = slots
            .map(slot => build.itemIds?.[slot])
            .filter(id => Number.isInteger(id) && id > 0);
        ids.push(...groupIds);
        if (groupIds.length !== 1) counts[group] = groupIds.length;
    });
    return { ids, counts };
}

/**
 * The `stuff=` payload for a build, base64 of the MessagePack map.
 * Exported for the parity test; callers want `dofusbookUrl`.
 */
export function dofusbookStuffParam(build) {
    const { ids, counts } = itemGroups(build);
    const payload = {
        0: baseSheet(build),
        1: DOFUSBOOK_POINT_ORDER.map(name => build.attributes?.[STAT_ID_BY_NAME.get(name)] || 0),
        2: build.lvl,
        3: 0,
        4: counts,
        5: ids,
    };
    const bytes = [];
    pack(payload, bytes);
    return toBase64(bytes);
}

/** The DofusBook stuffer URL that opens this build. */
export function dofusbookUrl(build) {
    return `${DOFUSBOOK_STUFFER_URL}?stuff=${encodeURIComponent(dofusbookStuffParam(build))}`;
}
